import React from "react";
import { useQuery } from "@tanstack/react-query";
import { PageContainer } from "../../components/layout/PageContainer";
import { Card } from "../../ui/Card";
import { Input } from "../../ui/Input";
import { Button } from "../../ui/Button";
import { AuditLog } from "../../types/log";
import { listLogs } from "../../api/logs";

const PAGE_SIZE = 8;


function formatTimestamp(value: string) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function displayOrDash(value?: string | null) {
  return value && value.trim() !== "" ? value : "-";
}

export default function LogsPage() {
  const [startAt, setStartAt] = React.useState<string>("");
  const [endAt, setEndAt] = React.useState<string>("");
  const [cursor, setCursor] = React.useState<string | null>(null);
  const [cursorStack, setCursorStack] = React.useState<string[]>([]);

  React.useEffect(() => {
    setCursor(null);
    setCursorStack([]);
  }, [startAt, endAt]);

  const { data, isLoading } = useQuery({
    queryKey: ["logs", cursor, startAt, endAt],
    queryFn: () =>
      listLogs({
        cursor,
        limit: PAGE_SIZE,
        startAt: startAt || undefined,
        endAt: endAt || undefined,
      }),
    staleTime: 15000,
  });

  const logs: AuditLog[] = data?.items ?? [];
  const total = data?.total ?? 0;
  const page = cursorStack.length + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startIndex = (page - 1) * PAGE_SIZE;
  const showingFrom = total === 0 ? 0 : startIndex + 1;
  const showingTo = startIndex + logs.length;

  return (
    <PageContainer
      title="Audit Logs"
      description="admin_logs table fields with timestamp range filtering and pagination."
      width="full"
      className="h-full min-h-0 flex flex-col"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <Card>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="grid w-full gap-2 sm:max-w-[520px] sm:grid-cols-2">
              <div className="grid gap-1.5">
                <span className="text-xs font-semibold text-[color:rgba(255,255,255,0.9)]">
                  Start timestamp
                </span>
                <Input
                  type="datetime-local"
                  value={startAt}
                  onChange={(e) => setStartAt(e.target.value)}
                  placeholder="YYYY-MM-DD HH:MM"
                  aria-label="Start timestamp"
                />
              </div>

              <div className="grid gap-1.5">
                <span className="text-xs font-semibold text-[color:rgba(255,255,255,0.9)]">
                  End timestamp
                </span>
                <Input
                  type="datetime-local"
                  value={endAt}
                  onChange={(e) => setEndAt(e.target.value)}
                  placeholder="YYYY-MM-DD HH:MM"
                  aria-label="End timestamp"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setCursor(null);
                  setCursorStack([]);
                }}
              >
                Apply
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setStartAt("");
                  setEndAt("");
                  setCursor(null);
                  setCursorStack([]);
                }}
              >
                Clear
              </Button>
            </div>
          </div>

          <div className="mt-3 text-xs text-[color:var(--kkp-text-muted)]">
            Only timestamp range filtering is available today. Pagination is
            client-side now and ready to be wired to cursor-based API responses
            later.
          </div>
        </Card>

        <Card className="p-0 overflow-hidden flex flex-col min-h-0">
          <div className="flex flex-col gap-2 border-b border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(255,255,255,0.02)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-white">
                Audit log records
              </div>
              <div className="text-xs text-[color:var(--kkp-text-muted)]">
                Showing {showingFrom}-{showingTo} of {total} total logs.
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs text-[color:var(--kkp-text-muted)]">
              <span className="rounded-full border border-[color:var(--kkp-border)] bg-[color:rgba(255,255,255,0.03)] px-3 py-1.5 font-semibold text-white">
                Page {page} / {totalPages}
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (cursorStack.length === 0) return;
                  const nextStack = [...cursorStack];
                  const prevCursor = nextStack.pop() ?? null;
                  setCursorStack(nextStack);
                  setCursor(prevCursor || null);
                }}
                disabled={cursorStack.length === 0}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (!data?.nextCursor) return;
                  setCursorStack((prev) => [...prev, cursor ?? ""]);
                  setCursor(data.nextCursor);
                }}
                disabled={!data?.nextCursor}
              >
                Next
              </Button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[color:var(--kkp-surface-2)]">
                <tr className="text-[11px] font-bold uppercase tracking-wide text-[color:rgba(255,255,255,0.75)]">
                  <th className="px-4 py-3">Seq ID</th>
                  <th className="px-4 py-3">Timestamp</th>
                  <th className="px-4 py-3">Actor</th>
                  <th className="px-4 py-3">IP Address</th>
                  <th className="px-4 py-3">Country</th>
                  <th className="px-4 py-3">Region</th>
                  <th className="px-4 py-3">City</th>
                  <th className="px-4 py-3">User Agent</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Details</th>
                </tr>
              </thead>

              <tbody>
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-6 text-center text-sm text-[color:var(--kkp-text-muted)]"
                    >
                      Loading logs…
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-6 text-center text-sm text-[color:var(--kkp-text-muted)]"
                    >
                      No logs in the selected time range.
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr
                      key={log.seqId}
                      className="border-t border-[color:rgba(255,255,255,0.08)]"
                    >
                      <td className="px-4 py-3 font-semibold text-white">
                        {log.seqId}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="px-4 py-3 text-white">{log.actor}</td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {displayOrDash(log.ipAddress)}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {displayOrDash(log.country)}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {displayOrDash(log.region)}
                      </td>
                      <td className="px-4 py-3 text-[color:var(--kkp-text-muted)]">
                        {displayOrDash(log.city)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-[280px] truncate text-[color:rgba(255,255,255,0.86)]">
                          {displayOrDash(log.userAgent)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="truncate font-semibold text-white">
                          {log.reason}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="max-w-[320px] truncate text-[color:rgba(255,255,255,0.86)]" title={log.details}>
                          {log.details}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-2 border-t border-[color:rgba(255,255,255,0.08)] bg-[color:rgba(255,255,255,0.02)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs text-[color:var(--kkp-text-muted)]">
              Cursor-based pagination can drop in by swapping the page state for
              a `cursor` token from the API response.
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setCursor(null);
                  setCursorStack([]);
                }}
                disabled={cursorStack.length === 0}
              >
                First page
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (cursorStack.length === 0) return;
                  const nextStack = [...cursorStack];
                  const prevCursor = nextStack.pop() ?? null;
                  setCursorStack(nextStack);
                  setCursor(prevCursor || null);
                }}
                disabled={cursorStack.length === 0}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (!data?.nextCursor) return;
                  setCursorStack((prev) => [...prev, cursor ?? ""]);
                  setCursor(data.nextCursor);
                }}
                disabled={!data?.nextCursor}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </PageContainer>
  );
}
